import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Table, Button, DatePicker, message, Input, Select } from 'antd';
import Card from '../../../components/bootstrap/card';
import { Col, Row } from 'react-bootstrap';
import { Excel } from 'antd-table-saveas-excel';
import { getClearanceTimeExportReportService } from '../../../services/ClearanceReportService';
import { getAllAdminIECsService } from '../../../services/FileDataService';
import { getBRCorelationService } from '../../../services/MasterService';
import moment from 'moment';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const ClearanceTimeExportReport = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );
  const tableRef = useRef(null);
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNo, setBillNo] = useState("");
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const tableStyles = `
    .clearance-export-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .clearance-export-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .clearance-export-table .ant-table-container {
      overflow-x: auto !important;
    }
    .clearance-export-table .ant-table-header {
      overflow: hidden !important;
    }
    .clearance-export-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchData();
    }
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, []);

  useEffect(() => {
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, [selectedIEC]);

  const fetchData = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes || null,
    };
    try {
      console.log('Fetching clearance time export report data...');
      const response = await getClearanceTimeExportReportService(selectedIEC, filters);
      // const processedData = preprocessData(response);
      setData(response);
    } catch (error) {
      console.error('Error fetching clearance time export report data:', error);
    }
  };

  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return ''; // Return empty string if the dateString is not a valid date
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  const preprocessData = (data) => {
    return data.map(record => {
      // record.daysSubToLeo = calculateDaysDifference(record.submission_date, record.leo_date);

      record.sb_date = record.sb_date ? formatDateToDDMMYY(new Date(record.sb_date)) : '';
      record.leo_date = record.leo_date ? formatDateToDDMMYY(new Date(record.leo_date)) : '';
      record.submission_date = record.submission_date ? formatDateToDDMMYY(new Date(record.submission_date)) : '';

      return record;
    });
  };

  const calculateDaysDifference = (date1, date2) => {
    const diffTime = Math.abs(new Date(date2) - new Date(date1));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const columns = [
    { title: 'BUSINESS UNIT', dataIndex: 'business_unit', key: 'business_unit', header: 'BUSINESS UNIT', width: 160, fixed: 'left' },
    { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', header: 'PLANT NAME', width: 150, fixed: 'left' },
    { title: 'IEC/BR CODE', dataIndex: 'iec_br', key: 'iec_br', header: 'IEC/BR CODE', width: 140 },
    { title: 'CB CODE', dataIndex: 'cb_code', key: 'cb_code', header: 'CB CODE', width: 110 },
    { title: 'NAME', dataIndex: 'cb_name', key: 'cb_name', header: 'NAME', width: 100 },
    { title: 'PORT', dataIndex: 'port_code', key: 'port_code', header: 'PORT', width: 100 },
    { title: 'SHB NO.', dataIndex: 'sb_no', key: 'sb_no', header: 'SHB NO.', width: 115 },
    { title: 'SHB DT.', dataIndex: 'sb_date', key: 'sb_date', header: 'SHB DT.', width: 110 },
    { title: 'SUBMISSION DT.', dataIndex: 'submission_date', key: 'submission_date', header: 'SUBMISSION DT.', width: 160 },
    { title: 'LEO DT.', dataIndex: 'leo_date', key: 'leo_date', header: 'LEO DT.', width: 110 },
    { title: 'DAYS SUB. TO LEO', dataIndex: 'days_sub_to_leo', key: 'days_sub_to_leo', header: 'DAYS SUB. TO LEO', align: 'right', width: 175 },
    { title: 'TERMS', dataIndex: 'invterm', key: 'invterm', header: 'TERMS', width: 100 },
    { title: 'MODE', dataIndex: 'mode', key: 'mode', header: 'MODE', width: 90 },
    { title: 'TAT', dataIndex: 'tat', key: 'tat', header: 'TAT', width: 80 },
    { title: 'REMARK', dataIndex: 'remark', key: 'remark', header: 'REMARK', width: 120 },
    { title: 'EGM No', dataIndex: 'egm_no', key: 'egm_no', header: 'EGM No', width: 130 },
    {
      title: 'EGM Dtd.', dataIndex: 'egm_date', key: 'egm_date', header: 'EGM Dtd.', width: 120,
      render: (text, record) => record.egm_date ? formatDateToDDMMYY(new Date(record.egm_date)) : ''
    },
    { title: 'EGM Remarks', dataIndex: 'egm_remarks', key: 'egm_remarks', header: 'EGM Remarks', width: 140 },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('ClearanceTimeExport', columns);


  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }
  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }
    if (dates.length === 2 && isValid) {
      fetchData();
    }
    else {
      message.error("Please select a valid date.");
    }
  };
  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
    }
  };


  const handleBillSearch = (value) => {
    setBillNo(value);
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async () => {
    let workbook = new ExcelJS.Workbook();
    let worksheet = workbook.addWorksheet('Data');


    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        submission_date: getDateOnly(obj.submission_date),
        sb_date: getDateOnly(obj.sb_date),
        leo_date: getDateOnly(obj.leo_date),
        egm_date: getDateOnly(obj.egm_date)
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    saveAs(blob, `Lucrative's CIP DSS - Clearance Time Export as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
  };

  const getDataToRender = () => {
    if (tableRef.current) {
      return data;
    }
    return [];
  };

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">
                  Lucrative's CIP TAT - Export Clearance as on {moment().format('DD/MM/YYYY')} of {selectedIECName}
                </h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button className="btn btn-primary cip-btn-submit" onClick={exportToExcel}>Export to Excel</button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              <ReportDateFilter dates={dates} onChange={handleDateChange} />
              <Input
                placeholder="Enter Bill No"
                allowClear
                value={billNo}
                onChange={(e) => handleBillSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                onClick={handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              <Table
                className="clearance-export-table"
                dataSource={data}
                columns={filteredColumns}
                ref={tableRef}
                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}

              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default ClearanceTimeExportReport;
